import React, { useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { useNavigate } from "react-router-dom";
import {
  ClipboardCheck,
  Building2,
  Ruler,
  FlaskConical,
  Droplets,
  Hammer,
  ChevronRight,
  X
} from "lucide-react";

const ServicesOverview = () => {
  const navigate = useNavigate();
  const [showTestingMenu, setShowTestingMenu] = useState(false);

  // Routes copied exactly from HomeIndex's testingSubMenu
  const testingSubMenu = [
    { title: "Aggregate Testing", link: "/AggregateTestingSection" },
    { title: "Cement Testing", link: "/CementTestingSection" },
    { title: "Concrete Testing", link: "/ConcreteTestingSection" },
    { title: "Soil Testing", link: "/SoilTestingSection" },
    { title: "Bitumen Testing", link: "/BitumenTestingSection" },
    { title: "Bricks, Blocks, Tiles & Pavers Testing", link: "/BricksBlocksTilesPaversTestingSection" },
    { title: "Steel Testing", link: "/SteelTestingSection" },
    { title: "Water & Wastewater Testing", link: "/WaterWastewaterTestingSection" },
  ];

  const services = [
    {
      icon: <ClipboardCheck />,
      title: "Advanced & Specialized Services",
      desc: "Precision-driven evaluations for infrastructure, materials, and structures ensuring reliability and performance.",
      link: "/AdvancedSpecializedServicesSection",
    },
    {
      icon: <Building2 />,
      title: "Design & Structural Consultancy",
      desc: "End-to-end structural design solutions integrating safety, sustainability, and architectural excellence.",
      link: "/DesignStructuralConsultancy",
    },
    {
      icon: <Ruler />,
      title: "Surveying",
      desc: "High-precision topographical and layout surveying using advanced Total Station and digital tools.",
      link: "/Surveying",
    },
    {
      icon: <FlaskConical />,
      title: "General Material Testing",
      desc: "Comprehensive testing for aggregates, cement, concrete, soil, and bitumen as per IS and ASTM standards.",
      link: null, // Set to null to trigger sidebar logic
      isSidebarTrigger: true, // Flag to identify this specific card
    },
    {
      icon: <Hammer />,
      title: "Bricks, Blocks & Steel Testing",
      desc: "Evaluation of bricks, blocks, pavers, and reinforcement steel ensuring safety and structural compliance.",
      link: "/BricksBlocksTilesPaversTestingSection",
    },
    {
      icon: <Droplets />,
      title: "Water & Wastewater Testing",
      desc: "Chemical and physical analysis for construction and environmental compliance as per IS and APHA standards.",
      link: "/WaterWastewaterTestingSection",
    },
  ];

  const handleCardClick = (svc) => {
    if (svc.isSidebarTrigger) {
      setShowTestingMenu(true);
    } else if (svc.link) {
      navigate(svc.link);
    }
  };

  const palette = [
    "bg-amber-500 text-white",
    "bg-gray-600 text-white",
    "bg-sky-500 text-white",
    "bg-cyan-500 text-white",
    "bg-rose-500 text-white",
    "bg-emerald-500 text-white",
  ];

  return (
    <>
      {/* ================= SIDE DRAWER FOR MATERIAL TESTING ================= */}
      {/* This matches the logic/styling from HomeIndex exactly */}
      <AnimatePresence>
        {showTestingMenu && (
          <>
            {/* Backdrop */}
            <motion.div
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              exit={{ opacity: 0 }}
              onClick={() => setShowTestingMenu(false)}
              className="fixed inset-0 bg-black/60 backdrop-blur-sm z-[65]"
            />
            
            {/* Drawer */}
            <motion.div
              initial={{ x: "100%" }}
              animate={{ x: 0 }}
              exit={{ x: "100%" }}
              transition={{ type: "spring", stiffness: 300, damping: 30 }}
              className="
                fixed top-0 right-0 h-full
                w-[85vw] md:w-[400px]
                bg-[#121212] border-l border-[#F37021]/30
                shadow-2xl z-[70] flex flex-col
              "
            >
              <div className="p-6 bg-gradient-to-br from-[#1a1a1a] to-[#0f0f0f] border-b border-[#F37021]/20 flex justify-between items-center">
                <div>
                  <h3 className="text-xl font-bold text-white">
                    Material Testing
                  </h3>
                  <p className="text-xs text-gray-400 mt-1">
                    Select a category below
                  </p>
                </div>
                <button 
                  onClick={() => setShowTestingMenu(false)}
                  className="p-2 hover:bg-white/10 rounded-full text-white transition"
                >
                  <X size={20} />
                </button>
              </div>

              <div className="flex-1 overflow-y-auto p-4 space-y-2">
                {testingSubMenu.map((item, i) => (
                  <motion.button
                    key={i}
                    whileHover={{ x: 4, backgroundColor: "rgba(243, 112, 33, 0.1)" }}
                    onClick={() => {
                        setShowTestingMenu(false);
                        navigate(item.link);
                    }}
                    className="
                      w-full p-4 text-left rounded-lg
                      border border-white/5
                      hover:border-[#F37021]/40
                      text-gray-300 hover:text-white
                      flex justify-between items-center
                      transition-all
                    "
                  >
                    <span className="font-medium">{item.title}</span>
                    <ChevronRight size={16} className="text-[#F37021]" />
                  </motion.button>
                ))}
              </div>
            </motion.div>
          </>
        )}
      </AnimatePresence>

      <section className="relative py-8 px-4 md:px-8">
        <div className="max-w-7xl mx-auto">
          <div className="flex flex-wrap -mx-3">
            {services.map((svc, idx) => {
              const color = palette[idx % palette.length];
              return (
                <motion.div
                  key={idx}
                  className={`
                    p-3 
                    w-full sm:w-[calc(50%-1.5rem)] lg:w-[calc(33.333%-1.5rem)]
                  `}
                  initial={{ opacity: 0, x: -24 }}
                  animate={{ opacity: 1, x: 0 }}
                  transition={{ delay: idx * 0.05, type: "spring", stiffness: 120 }}
                >
                  <motion.button
                    onClick={() => handleCardClick(svc)}
                    whileTap={{ scale: 0.98 }}
                    whileHover={{ y: -4 }}
                    className={`
                      ${color} 
                      w-full h-full
                      rounded-md shadow-md overflow-hidden cursor-pointer
                      flex flex-col justify-between
                    `}
                  >
                    <div className="flex items-center justify-between w-full h-full">
                      <div className="flex items-center px-6 py-6 w-full">
                        <div className="mr-4 flex-shrink-0">
                          <div className="w-[46px] h-[46px] bg-white/10 rounded-md flex items-center justify-center">
                            {React.cloneElement(svc.icon, { className: "w-5 h-5" })}
                          </div>
                        </div>

                        <div className="text-left flex-grow">
                          <div className="font-serif text-lg md:text-xl leading-tight tracking-wide font-medium">
                            {svc.title}
                          </div>

                          <div className="hidden md:block text-xs md:text-sm opacity-90 mt-2 leading-relaxed">
                            {svc.desc}
                          </div>
                        </div>
                      </div>

                      <div className="pr-4 pl-2 flex items-center justify-center h-full">
                        <ChevronRight className="w-6 h-6 opacity-70" />
                      </div>
                    </div>
                  </motion.button>
                </motion.div>
              );
            })}
          </div>
        </div>
      </section>
    </>
  );
};

export default ServicesOverview;